// Plan/pricing constants shared by the host controller and the upgrade page.

// Copresenter seats on the free plan.
//
// ZERO: copresenting is a paid feature. A free host presents alone (outside
// the free copresent pass); Pro hosts are unlimited.
//
// The BACKEND is the authority — it enforces the cap and sends the limit it
// actually applied on the JOIN reply. This constant is only for copy and for
// the display fallback when that flag is absent (older backend, or a message
// that doesn't carry it). Keep in sync with Plans.DefaultFreeCoPresenterSeats.
//
// The fallback direction matters now that it is zero. An older backend that
// sends no limit used to fall back to "1 seat", which merely understated a
// room that had more; falling back to zero instead says "you cannot do this at
// all". That is the right default for copy — it matches what the server will
// actually do — but it is why nothing here may DECIDE anything. Every gate is
// the backend's answer; this only labels it.
export const FREE_COPRESENTER_SEATS = 0;

export type BillingInterval = "month" | "year";

// What we SAY Pro costs. Stripe is the authority on what is actually charged:
// the account service picks the price id for the chosen interval. These are
// copy only, shared by the upgrade page, the controller's dialog and the
// receipt so no two can quote different numbers at one buyer. Update alongside
// the Stripe prices (and website/src/data/pricing.ts, and the Resend
// templates' PRICE_* variables in api/src/lifecycle/messages.ts).
//
// Yearly is 50% off twelve months of monthly: $48 → $24, i.e. $2 a month.
//
// Two pairs per interval, on purpose. `amount`/`per` is what is CHARGED each
// period — the buy button and the receipt say this, because the number next to
// a payment must be the payment. `perMonth` is what the price card LEADS with,
// so monthly and yearly compare like with like ($4 vs $2 a month).
export const PRICES: Record<
  BillingInterval,
  { amount: string; per: string; perMonth: string; note: string; label: string }
> = {
  month: { amount: "$4", per: "/ month", perMonth: "$4", note: "USD · billed monthly", label: "Monthly" },
  year: { amount: "$24", per: "/ year", perMonth: "$2", note: "USD · billed $24 yearly", label: "Yearly" },
};

export const YEARLY_SAVING = "Save 50%";

// The interval shown first. Yearly: it is the better deal for the buyer and
// the one that survives the gap between one presentation and the next.
export const DEFAULT_INTERVAL: BillingInterval = "year";

export const parseInterval = (value: string | null | undefined): BillingInterval | null =>
  value === "month" || value === "year" ? value : null;
